import { useState } from "react";
import { Phone, Mail, MapPin, Clock } from "lucide-react";
import { toast } from "sonner";
import { FIRM } from "@/data/content";
import { PageHero, Breadcrumbs, FadeIn } from "@/components/Shared";

const inputCls =
  "brand-input";

const initialForm = {
  firstName: "",
  lastName: "",
  email: "",
  phone: "",
  funding: "",
  source: "",
  message: "",
};

export default function Contact() {
  const [form, setForm] = useState(initialForm);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.firstName || !form.lastName || !form.email || !form.message) {
      toast.error("Please complete your name, email and message.");
      return;
    }
    toast.success("Thank you — your enquiry has been received. We will be in touch shortly.");
    setForm(initialForm);
  };

  return (
    <main data-testid="contact-page">
      <PageHero
        eyebrow="Included in The Times Best Law Firm 2026"
        title="Contact Us"
        lead="Speak to our award winning legal team now. For urgent matters, our emergency line is answered 24 hours a day."
      >
        <div className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center sm:gap-8">
          <a
            href={`tel:${FIRM.phone.replace(/\s/g, "")}`}
            className="flex items-center gap-3 text-lg text-white transition-colors hover:text-brand-gold"
            data-testid="contact-hero-office"
          >
            <Phone className="size-5 text-brand-gold" />
            <span>
              <span className="mr-2 text-[10px] uppercase tracking-[0.2em] text-slate-400">Office:</span>
              {FIRM.phone}
            </span>
          </a>
          <a
            href={`tel:${FIRM.emergencyPhone.replace(/\s/g, "")}`}
            className="flex items-center gap-3 text-lg text-white transition-colors hover:text-brand-gold"
            data-testid="contact-hero-emergency"
          >
            <Phone className="size-5 text-brand-terracotta" />
            <span>
              <span className="mr-2 text-[10px] uppercase tracking-[0.2em] text-slate-400">24hr</span>
              {FIRM.emergencyPhone}
            </span>
          </a>
          <a
            href={`mailto:${FIRM.email}`}
            className="flex items-center gap-3 text-lg text-white transition-colors hover:text-brand-gold"
            data-testid="contact-hero-email"
          >
            <Mail className="size-5 text-brand-terracotta" />
            {FIRM.email}
          </a>
        </div>
      </PageHero>
      <Breadcrumbs items={[{ label: "Contact" }]} />

      <section className="bg-white py-20 sm:py-28 lg:py-32">
        <div className="brand-container">
          <div className="grid grid-cols-1 gap-12 lg:grid-cols-12">
            {/* Form */}
            <div className="lg:col-span-7">
              <FadeIn>
                <h2 className="brand-heading text-2xl sm:text-3xl">
                  Make an enquiry
                </h2>
                <p className="mt-3 text-sm leading-relaxed text-slate-500">
                  For urgent assistance please call our 24 hour emergency line rather
                  than using this form.
                </p>
                <form onSubmit={handleSubmit} className="mt-8 space-y-5" data-testid="enquiry-form">
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <input className={inputCls} placeholder="First name *" value={form.firstName} onChange={set("firstName")} data-testid="input-first-name" />
                    <input className={inputCls} placeholder="Last name *" value={form.lastName} onChange={set("lastName")} data-testid="input-last-name" />
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <input className={inputCls} type="email" placeholder="Email *" value={form.email} onChange={set("email")} data-testid="input-email" />
                    <input className={inputCls} type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} data-testid="input-phone" />
                  </div>
                  <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                    <select className={inputCls} value={form.funding} onChange={set("funding")} data-testid="select-funding">
                      <option value="">Type of funding</option>
                      <option value="private">Private</option>
                      <option value="legal-aid">Legal Aid</option>
                      <option value="unknown">Don't know</option>
                    </select>
                    <select className={inputCls} value={form.source} onChange={set("source")} data-testid="select-source">
                      <option value="">Where did you hear about us?</option>
                      <option>Existing client</option>
                      <option>Represented previously</option>
                      <option>Google search</option>
                      <option>Social media</option>
                      <option>Referred by barrister</option>
                      <option>Referred by solicitor</option>
                      <option>Other</option>
                    </select>
                  </div>
                  <textarea
                    className={`${inputCls} min-h-[140px] resize-y`}
                    placeholder="Your message *"
                    value={form.message}
                    onChange={set("message")}
                    data-testid="input-message"
                  />
                  <p className="text-xs leading-relaxed text-slate-400">
                    By submitting this form you consent to us contacting you about
                    your enquiry. We treat all enquiries in the strictest confidence.
                  </p>
                  <button
                    type="submit"
                    className="brand-cta-primary px-10 py-4 tracking-[0.2em]"
                    data-testid="enquiry-submit-button"
                  >
                    Send enquiry
                  </button>
                </form>
              </FadeIn>
            </div>

            {/* Location panel */}
            <aside className="lg:col-span-5">
              <FadeIn delay={0.15}>
                <div className="brand-panel-dark p-8 sm:p-10" data-testid="office-location-panel">
                  <p className="brand-eyebrow-on-dark">
                    Our office
                  </p>
                  <h3 className="mt-3 text-2xl font-light tracking-tight">{FIRM.fullName}</h3>
                  <address className="mt-6 space-y-4 not-italic">
                    <p className="flex items-start gap-3 text-sm text-slate-200">
                      <MapPin className="mt-0.5 size-5 shrink-0 text-brand-gold" />
                      <span>
                        {FIRM.address.map((line, i) => (
                          <span key={i} className="block">{line}</span>
                        ))}
                      </span>
                    </p>
                    <p className="flex items-center gap-3 text-sm text-slate-200">
                      <Phone className="size-5 shrink-0 text-brand-gold" />
                      <span>
                        Office:{" "}
                        <a href={`tel:${FIRM.phone.replace(/\s/g, "")}`} className="transition-colors hover:text-brand-gold" data-testid="contact-office-phone">
                          {FIRM.phone}
                        </a>
                      </span>
                    </p>
                    <p className="flex items-center gap-3 text-sm text-slate-200">
                      <Phone className="size-5 shrink-0 text-brand-terracotta" />
                      <span>
                        Emergency (24hr):{" "}
                        <a href={`tel:${FIRM.emergencyPhone.replace(/\s/g, "")}`} className="transition-colors hover:text-brand-gold" data-testid="contact-emergency-phone">
                          {FIRM.emergencyPhone}
                        </a>
                      </span>
                    </p>
                    <p className="flex items-center gap-3 text-sm text-slate-200">
                      <Mail className="size-5 shrink-0 text-brand-gold" />
                      <a href={`mailto:${FIRM.email}`} className="transition-colors hover:text-brand-gold" data-testid="contact-office-email">
                        {FIRM.email}
                      </a>
                    </p>
                    <p className="flex items-center gap-3 text-sm text-slate-200">
                      <Clock className="size-5 shrink-0 text-brand-gold" />
                      Opening times: {FIRM.hours}
                    </p>
                  </address>
                </div>
                <div className="mt-6 border border-slate-200">
                  <iframe
                    title="Hallinans office location"
                    src="https://www.google.com/maps?q=Alexandra+House,+55A+Catherine+Place,+London+SW1E+6DY&output=embed"
                    className="h-72 w-full"
                    loading="lazy"
                    data-testid="office-map"
                  />
                </div>
              </FadeIn>
            </aside>
          </div>
        </div>
      </section>
    </main>
  );
}
